import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ModuleId } from '@/constants/modules'

export interface PageBreadcrumb {
  label: string
  to?: string
}

export interface PageChromeState {
  title: string
  mobileTitle?: string
  moduleId?: ModuleId
  icon?: LucideIcon
  actions?: ReactNode
  secondaryActions?: ReactNode
  /** When `workspace`, AppHeader shows utility/search layout; page owns title. */
  identityPlacement?: 'chrome' | 'workspace'
  breadcrumbs?: PageBreadcrumb[]
  /** Optional centered header search (page-owned filter, not global invent). */
  headerSearch?: ReactNode
}

const DEFAULT_CHROME: PageChromeState = {
  title: 'Dashboard',
  identityPlacement: 'chrome',
}

interface PageChromeContextValue {
  chrome: PageChromeState
  setChrome: (chrome: PageChromeState) => void
  resetChrome: () => void
}

const PageChromeContext = createContext<PageChromeContextValue | null>(null)

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChromeState] = useState<PageChromeState>(DEFAULT_CHROME)

  const value = useMemo(
    () => ({
      chrome,
      setChrome: setChromeState,
      resetChrome: () => setChromeState(DEFAULT_CHROME),
    }),
    [chrome],
  )

  return (
    <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>
  )
}

export function usePageChrome() {
  const ctx = useContext(PageChromeContext)
  if (!ctx) {
    throw new Error('usePageChrome must be used within PageChromeProvider')
  }
  return ctx
}

/** Sync page chrome into the global app header bar */
export function useRegisterPageChrome({
  title,
  mobileTitle,
  moduleId,
  icon,
  actions,
  secondaryActions,
  identityPlacement = 'chrome',
  breadcrumbs,
  headerSearch,
}: PageChromeState) {
  const { setChrome } = usePageChrome()

  useLayoutEffect(() => {
    setChrome({
      title,
      mobileTitle,
      moduleId,
      icon,
      actions,
      secondaryActions,
      identityPlacement,
      breadcrumbs,
      headerSearch,
    })
  }, [
    title,
    mobileTitle,
    moduleId,
    icon,
    actions,
    secondaryActions,
    identityPlacement,
    breadcrumbs,
    headerSearch,
    setChrome,
  ])
}
